/**
 * Launcher entry. Loads the build-time-generated games index, splits it
 * into three tabs, and renders the active tab as a responsive card grid:
 *
 *   1. Most played   — top N games by local play count, only available
 *                      when the user has launched at least one game on
 *                      this device. Counts come from `localStorage`
 *                      under `launcher.playCounts`.
 *   2. Single player — games whose manifest declares `players.min === 1`
 *                      (i.e. you can start a match alone).
 *   3. Multiplayer   — games whose manifest declares `players.min >= 2`.
 *
 * Categories are mutually exclusive: a game with `1..N` players is
 * "single player" because the player can start alone — even though it
 * also supports multiplayer once friends join. The launcher's job is to
 * answer "can I start this right now, alone?".
 *
 * Tab state lives in `window.location.hash` (`#most`, `#single`, `#multi`)
 * so deep links + the browser back button just work. The active tab is
 * also persisted to `localStorage` so the launcher reopens to where you
 * left off.
 *
 * The launcher re-renders on `visibilitychange → visible` so play counts
 * update the moment you switch back from a game tab.
 *
 * The index is produced by `tools/build-games-index.mjs`, which scans
 * `games/<id>/manifest.json` and writes `packages/launcher/public/games.json`
 * before each Vite dev/build run.
 *
 * Games themselves are NOT served from the same host as the launcher —
 * they live on `applications.bapp.ro` (uploaded by the CI `deploy-games`
 * job via FTP). `VITE_GAMES_BASE_URL` is inlined by Vite at build time
 * so we can point at that host without any runtime config.
 *
 * Default: `https://applications.bapp.ro/games`
 * Override: set `VITE_GAMES_BASE_URL` in the build environment (the
 * Dockerfile passes it as an `ARG`).
 */
const GAMES_BASE_URL = (
  (import.meta.env && import.meta.env.VITE_GAMES_BASE_URL) ||
  'https://applications.bapp.ro/games'
).replace(/\/+$/, '');

const PLAY_COUNTS_KEY = 'launcher.playCounts';
const ACTIVE_TAB_KEY  = 'launcher.activeTab';
const MOST_PLAYED_LIMIT = 8;

const TAB_MOST   = 'most';
const TAB_SINGLE = 'single';
const TAB_MULTI  = 'multi';

const TAB_LABELS = {
  [TAB_MOST]:   'Most played',
  [TAB_SINGLE]: 'Single player',
  [TAB_MULTI]:  'Multiplayer'
};

const root = document.getElementById('grid');

let _games = [];
let _activeTab = null;

// ── play-count storage ───────────────────────────────────────────────

function loadPlayCounts() {
  try {
    const raw = localStorage.getItem(PLAY_COUNTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
}

function savePlayCounts(counts) {
  try {
    localStorage.setItem(PLAY_COUNTS_KEY, JSON.stringify(counts));
  } catch (_) { /* private mode / quota — ignore */ }
}

function bumpPlayCount(id) {
  const counts = loadPlayCounts();
  counts[id] = (counts[id] || 0) + 1;
  savePlayCounts(counts);
}

// ── classification ───────────────────────────────────────────────────

function isSinglePlayer(g) {
  // `min === 1` means a single human can start the match. The game may
  // ALSO support multiplayer (max > 1) — the launcher still classifies
  // it as single-player here because the question we're answering is
  // "can I play this alone?".
  return g && g.players && g.players.min === 1;
}

function isMultiplayer(g) {
  return g && g.players && g.players.min >= 2;
}

function gamesForTab(tab, counts) {
  if (tab === TAB_MOST) {
    return _games
      .filter((g) => (counts[g.id] || 0) > 0)
      .sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0))
      .slice(0, MOST_PLAYED_LIMIT);
  }
  if (tab === TAB_SINGLE) return _games.filter(isSinglePlayer);
  if (tab === TAB_MULTI)  return _games.filter(isMultiplayer);
  return [];
}

// ── tab state ────────────────────────────────────────────────────────

function availableTabs(counts) {
  const tabs = [];
  if (Object.keys(counts).length) tabs.push(TAB_MOST);
  if (_games.some(isSinglePlayer)) tabs.push(TAB_SINGLE);
  if (_games.some(isMultiplayer))  tabs.push(TAB_MULTI);
  return tabs;
}

function pickInitialTab(counts) {
  const tabs = availableTabs(counts);
  if (!tabs.length) return null;
  // 1. Honor the URL hash if it points at an available tab.
  const fromHash = window.location.hash.replace(/^#/, '');
  if (tabs.includes(fromHash)) return fromHash;
  // 2. Otherwise restore the last tab from storage if it's still valid.
  let stored = null;
  try { stored = localStorage.getItem(ACTIVE_TAB_KEY); } catch (_) { /* ignore */ }
  if (stored && tabs.includes(stored)) return stored;
  // 3. Fall back to the first available tab — most-played wins if the
  // user has any plays, otherwise single-player, otherwise multiplayer.
  return tabs[0];
}

function setActiveTab(tab, opts = {}) {
  _activeTab = tab;
  try { localStorage.setItem(ACTIVE_TAB_KEY, tab); } catch (_) { /* ignore */ }
  const desiredHash = '#' + tab;
  if (window.location.hash !== desiredHash && !opts.skipHash) {
    // replaceState avoids polluting the back-button history with every
    // tab click; the user can still hit back to leave the launcher.
    history.replaceState(null, '', desiredHash);
  }
  render();
}

// ── render ───────────────────────────────────────────────────────────

async function loadGames() {
  try {
    const res = await fetch('./games.json', { cache: 'no-cache' });
    if (res.ok) _games = await res.json();
  } catch (_) {
    _games = [];
  }
}

function render() {
  if (!_games.length) {
    root.innerHTML = '<div class="empty">No games found yet. Run <code>npm run create-game my-game</code> from the repo root.</div>';
    return;
  }

  const counts = loadPlayCounts();
  const tabs = availableTabs(counts);

  // If the active tab disappeared (e.g. the user wiped storage and the
  // most-played tab is no longer available), fall back to the first
  // available tab.
  if (!tabs.includes(_activeTab)) {
    _activeTab = tabs[0] || null;
  }

  root.innerHTML = '';

  const tabBar = renderTabBar(tabs, counts);
  root.appendChild(tabBar);

  const panel = document.createElement('div');
  panel.className = 'tab-panel';
  panel.setAttribute('role', 'tabpanel');
  panel.id = 'tab-panel-' + _activeTab;

  const sectionGames = gamesForTab(_activeTab, counts);
  if (!sectionGames.length) {
    panel.appendChild(renderEmptyTab(_activeTab));
  } else {
    const grid = document.createElement('div');
    grid.className = 'card-grid';
    for (const g of sectionGames) {
      grid.appendChild(renderCard(g, counts));
    }
    panel.appendChild(grid);
  }
  root.appendChild(panel);
}

function renderTabBar(tabs, counts) {
  const bar = document.createElement('div');
  bar.className = 'tab-bar';
  bar.setAttribute('role', 'tablist');

  for (const tab of tabs) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tab-button' + (tab === _activeTab ? ' is-active' : '');
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-selected', tab === _activeTab ? 'true' : 'false');
    btn.setAttribute('aria-controls', 'tab-panel-' + tab);

    const label = document.createElement('span');
    label.className = 'tab-label';
    label.textContent = TAB_LABELS[tab];
    btn.appendChild(label);

    const count = document.createElement('span');
    count.className = 'tab-count';
    count.textContent = String(gamesForTab(tab, counts).length);
    btn.appendChild(count);

    btn.addEventListener('click', () => {
      if (tab !== _activeTab) setActiveTab(tab);
    });

    bar.appendChild(btn);
  }

  return bar;
}

function renderEmptyTab(tab) {
  const empty = document.createElement('div');
  empty.className = 'empty';
  if (tab === TAB_MOST) {
    empty.textContent = 'No plays yet — open a game to see it here.';
  } else {
    empty.textContent = 'No games in this section yet.';
  }
  return empty;
}

function renderCard(g, counts) {
  const a = document.createElement('a');
  a.className = 'card';
  // Absolute URL to the remote host where games are uploaded via FTP
  // by the CI `deploy-games` job.
  a.href = GAMES_BASE_URL + '/' + g.id + '/index.html';
  a.target = '_blank';
  a.rel = 'noopener';

  // Click fires before the new tab opens, so we don't need
  // preventDefault — the count is bumped synchronously and the
  // navigation continues. The launcher visually re-renders on
  // visibilitychange → visible (see `main()`), so the badge updates
  // automatically when the user switches back.
  a.addEventListener('click', () => {
    try { bumpPlayCount(g.id); } catch (_) { /* ignore */ }
  });

  const thumb = document.createElement('div');
  thumb.className = 'thumb';
  if (g.thumbnail) {
    const img = document.createElement('img');
    img.src = GAMES_BASE_URL + '/' + g.id + '/' + g.thumbnail;
    img.alt = g.name;
    img.loading = 'lazy';
    thumb.appendChild(img);
  } else {
    thumb.classList.add('placeholder');
    thumb.textContent = (g.name || g.id).slice(0, 1).toUpperCase();
  }

  const playCount = counts && counts[g.id];
  if (playCount) {
    const badge = document.createElement('div');
    badge.className = 'play-badge';
    badge.textContent = playCount + '×';
    badge.title = 'Played ' + playCount + ' time' + (playCount === 1 ? '' : 's');
    thumb.appendChild(badge);
  }

  const title = document.createElement('h2');
  title.textContent = g.name;

  const meta = document.createElement('div');
  meta.className = 'meta';
  meta.innerHTML =
    '<span>' + (g.players ? (g.players.min + '–' + g.players.max + ' players') : '') + '</span>' +
    (g.estimatedDurationSec ? '<span>' + Math.round(g.estimatedDurationSec / 60 * 10) / 10 + ' min</span>' : '') +
    (g.category ? '<span>' + g.category + '</span>' : '');

  const desc = document.createElement('p');
  desc.className = 'desc';
  desc.textContent = g.description || '';

  a.appendChild(thumb);
  a.appendChild(title);
  a.appendChild(meta);
  a.appendChild(desc);
  return a;
}

// ── boot ─────────────────────────────────────────────────────────────

async function main() {
  await loadGames();

  const counts = loadPlayCounts();
  _activeTab = pickInitialTab(counts);
  // Sync the URL hash to whatever we picked, without pushing history.
  if (_activeTab && window.location.hash !== '#' + _activeTab) {
    history.replaceState(null, '', '#' + _activeTab);
  }

  render();

  // Hash navigation (back button, deep links).
  window.addEventListener('hashchange', () => {
    const tab = window.location.hash.replace(/^#/, '');
    if (tab && tab !== _activeTab) setActiveTab(tab, { skipHash: true });
  });

  // Refresh play counts when returning from a game tab.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') render();
  });
}

main();
